import type { ConversationDocsDto } from '@chat-emp/shared';

import type { Message } from '../fixtures/domain.js';

/**
 * Rules about what is written on a conversation: a note the employer typed, and
 * a summary a model drew.
 *
 * Two entries of the same kind, and the only difference between them is who the
 * author was. Where they differ is in the prompt, not on screen: the note
 * reaches the model verbatim and outranks everything else, because a person
 * wrote it on purpose. Neither is ever shown to the jobseeker.
 *
 * The shapes live in `@chat-emp/shared` — they cross the BFF, so the client and
 * the server must not each keep their own idea of them.
 */
export type ConversationNote = NonNullable<ConversationDocsDto['note']>;
export type ConversationSummary = NonNullable<ConversationDocsDto['summary']>;

/**
 * A drawn summary before the store stamps it: the text, the watermark the server
 * returned, and the note it was drawn against, captured at draw time. `generatedAt`
 * is not here because it is not true yet — it is stamped when the employer keeps it.
 */
export type DrawnSummary = Omit<ConversationSummary, 'generatedAt'>;

/**
 * Whether a summary says anything at all.
 *
 * A model handed a two-message conversation can legitimately come back with
 * nothing, and that is a real answer rather than a failure — but it is not one
 * worth rendering an empty box for.
 */
export function hasContent(summary: ConversationSummary | null): boolean {
  return summary !== null && summary.text.trim() !== '';
}

/**
 * What a summary is drawn from: everything except the app's own system notes
 * ("You ended this chat."). Dropped here so the watermark and the transcript
 * agree about what counts — a trailing system note left in would keep the summary
 * permanently one message behind. The draw itself reads the transcript upstream;
 * this is only for the staleness the client computes from its own message window.
 */
export function summarisable(messages: Message[]): Message[] {
  return messages.filter((message) => !message.system);
}

/**
 * The messages a summary has not seen yet. A watermark that matches nothing
 * returns the whole conversation, which is the only safe reading: the message it
 * named is gone (deleted, or never in this client's window), so re-reading
 * everything is wrong only in cost, where serving the stale summary is wrong in
 * substance.
 */
export function pendingAfter(messages: Message[], summary: ConversationSummary | null): Message[] {
  const eligible = summarisable(messages);
  if (!summary) return eligible;
  const at = eligible.findIndex((message) => message.id === summary.coversUpTo);
  return at === -1 ? eligible : eligible.slice(at + 1);
}

/**
 * Whether the summary no longer speaks for what it was drawn from. Two inputs,
 * two ways to fall behind: the transcript can only grow (the watermark catches
 * it), the note can be rewritten in place (compared whole against the copy the
 * summary was drawn with). With no summary, stale means there is something to
 * draw from — an empty conversation is empty, not out of date.
 */
export function needsRedraw(
  messages: Message[],
  summary: ConversationSummary | null,
  noteText: string,
): boolean {
  if (!summary) return summarisable(messages).length > 0;
  return pendingAfter(messages, summary).length > 0 || summary.drawnWithNote !== noteText;
}
